import { Loader2Icon, SparklesIcon } from 'lucide-react'

import { IRQCategorySeverity } from '@/gen/inventory/v1/company_service_pb'
import { RiskLevel } from '@/gen/inventory/v1/risk_pb'

import { IrqCategoryList } from '@/pages/company-drawer/company-overview/set-irq-modal/irq-category-list'
import { SET_IRQ_MODAL_LABEL } from '@/pages/company-drawer/company-overview/set-irq-modal/labels'
import type { SourceGroupDisplay } from '@/pages/company-drawer/company-overview/set-irq-modal/lema-suggestions-card/types'
import { UserLastEngagementIrqUpdateInfo } from '@/pages/company-drawer/company-overview/user-last-engagement-irq-update-info/user-last-engagement-irq-update-info'
import { UserScoreInfo } from '@/pages/company-drawer/company-overview/user-score-info/user-score-info'

import { Alert, AlertIcon } from '@/components/lemmala-design-system/alert/alert'
import { Checkbox } from '@/components/lemmala-design-system/checkbox/checkbox'
import { Separator } from '@/components/lemmala-design-system/separator/separator'
import { RiskBadge } from '@/components/shared/risk-badge/risk-badge'

type OverviewStepProps = {
  companyId: string
  engagementId?: string
  projectedRiskLevel: RiskLevel
  currentRiskLevel: RiskLevel
  irqCategorySeverities: IRQCategorySeverity[]
  isEstimated: boolean
  isLoadingRisk: boolean
  sources: SourceGroupDisplay[]
  selectedSourceKeys: Set<string>
  onToggleSource: (key: string) => void
}

export const OverviewStep = ({
  companyId,
  engagementId,
  projectedRiskLevel,
  currentRiskLevel,
  irqCategorySeverities,
  isEstimated,
  isLoadingRisk,
  sources,
  selectedSourceKeys,
  onToggleSource,
}: OverviewStepProps) => {
  return (
    <div className='flex h-full flex-col rounded-md border border-main-border-default px-6'>
      <h3 className='py-4 text-main-foreground-primary text-heading-xxsmall'>
        {SET_IRQ_MODAL_LABEL.OVERVIEW}
      </h3>

      {isLoadingRisk ? (
        <div className='flex flex-1 items-center justify-center pb-6'>
          <Loader2Icon className='size-5 animate-spin text-main-foreground-tertiary' />
        </div>
      ) : (
        <div className='flex flex-1 items-start gap-2.5 pb-6'>
          {irqCategorySeverities.length > 0 && (
            <IrqCategoryList
              irqCategorySeverities={irqCategorySeverities}
              isEstimated={isEstimated}
            />
          )}

          <div className='flex min-w-0 flex-1 flex-col gap-4'>
            <div className='flex flex-col gap-2'>
              {isEstimated ? (
                <>
                  <div className='flex items-center gap-2'>
                    <span className='text-main-foreground-tertiary text-label-default'>
                      {SET_IRQ_MODAL_LABEL.ESTIMATED_IR_WITH_COLON}
                    </span>
                    <RiskBadge riskLevel={projectedRiskLevel} variant='estimated' />
                  </div>
                  <Alert
                    layout='vertical'
                    icon={<AlertIcon icon={SparklesIcon} />}
                    description={SET_IRQ_MODAL_LABEL.ESTIMATED_IR_DESCRIPTION}
                  />
                </>
              ) : (
                <>
                  <RiskBadge
                    riskLevel={currentRiskLevel}
                    variant='default'
                    size='large'
                    className='self-start'
                  />
                  {engagementId ? (
                    <UserLastEngagementIrqUpdateInfo
                      companyId={companyId}
                      engagementId={engagementId}
                    />
                  ) : (
                    <UserScoreInfo companyId={companyId} />
                  )}
                  <Separator orientation='horizontal' className='my-2' />
                  <div className='flex flex-col items-start gap-2'>
                    <div className='text-main-foreground-tertiary text-label-default'>
                      {SET_IRQ_MODAL_LABEL.CALCULATED_IR_WITH_COLON}
                    </div>
                    <RiskBadge riskLevel={projectedRiskLevel} variant='estimated' />
                  </div>
                </>
              )}
            </div>

            {sources.length > 0 && (
              <div className='mt-2 flex flex-col gap-2.5'>
                <p className='text-main-foreground-primary text-body-default'>
                  {SET_IRQ_MODAL_LABEL.PRE_FILL_ANSWERS_FROM}
                </p>
                <ul className='flex flex-col gap-3'>
                  {sources.map((source) => (
                    <li key={source.key} className='flex items-center gap-1.5'>
                      <Checkbox
                        id={`overview-source-${source.key}`}
                        checked={selectedSourceKeys.has(source.key)}
                        onCheckedChange={() => onToggleSource(source.key)}
                      />
                      <span className='inline-flex shrink-0'>{source.icon}</span>
                      <label
                        htmlFor={`overview-source-${source.key}`}
                        className='cursor-pointer text-main-foreground-primary text-body-default'
                      >
                        {source.name}
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
